<template>
  <div
    v-if="isShowWidget"
    class="checkout-delivery-widget"
    :class="{'checkout-delivery-widget_adaptive': adaptive}"
  >
    <ui-notification
      v-if="isStub"
      :title="widgetData.title"
      color="#FFB71B"
      class="checkout-delivery-widget__stub"
    >
      {{ widgetData.description }}
    </ui-notification>
    <div v-else class="checkout-delivery-widget__wrap">
      <ui-loader
        v-if="loading"
        size="30"
        class="checkout-delivery-widget__loader"
      />
      <template v-else>
        <div
          class="checkout-delivery-widget__text"
          :class="{ 'checkout-delivery-widget__text_only-title': !widgetData.description }"
        >
          <span class="checkout-delivery-widget__title">
            {{ widgetData.title }}
          </span>
          <br>
          <span v-if="widgetData.description">
            {{ widgetData.description }}
          </span>
        </div>

        <ui-button
          v-if="widgetData.action"
          :text="widgetData.action.text"
          wide
          secondary
          class="checkout-delivery-widget__action"
          @click="onClickAction(widgetData.action)"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { getLineIds } from '@/components/checkout/delivery-selector/utils';
import { SHIPPING_GROUP_NAMES, SHIPPING_METHODS } from '@/constants/delivery';
import { pluralize } from '@/utils/string-utils';
import {
  DELIVERY_SELECTOR_TYPES,
  WIDGET_PLACES,
  WIDGET_TYPES,
} from '@/components/checkout/checkout-delivery-widget/constants';
import {
  getPickupGroupsWithStoreType,
} from '@/components/checkout/checkout-delivery-widget/utils';

export default {
  name: 'checkout-delivery-widget',

  props: {
    adaptive: {
      type: Boolean,
      default: true,
    },
    place: { // место, где выводится виджет
      type: String,
      default: WIDGET_PLACES.checkout,
      validator: (value) => !!WIDGET_PLACES[value],
    },
  },

  data: () => ({
    points: [],
    loading: true,
  }),

  computed: {
    ...mapState({
      SHIPPING_CHANNELS: state => state.cart.channels,
    }),
    ...mapGetters({
      CITY: 'user/city',
      SHIPPING_GROUPS: 'cart/shippingGroups',
      PRODUCTS: 'cart/products',
      SUBTOTAL: 'cart/subtotal',
    }),

    isShowWidget() {
      if (!this.widgetData) return false;

      // не показываем заглушку и доставку одним заказом везде кроме корзины
      if (
        this.place !== WIDGET_PLACES.checkout
        && [WIDGET_TYPES.stub, WIDGET_TYPES.oneOrder].includes(this.widgetData.type)
      ) {
        return false;
      }

      // не показываем доставить курьером на попапе курьера
      if (this.place === WIDGET_PLACES.courier && this.widgetData?.type === WIDGET_TYPES.courier) {
        return false;
      }

      // не показываем самовывоз из магазина на попапе самовывоза и на карточке магазина
      if (
        [WIDGET_PLACES.pickup, WIDGET_PLACES.storeCard].includes(this.place)
        && [WIDGET_TYPES.oneStore, WIDGET_TYPES.onlyOneStore].includes(this.widgetData.type)
      ) {
        return false;
      }

      const isUnallocated = !!this.SHIPPING_GROUPS
        .some(({ type }) => type === SHIPPING_GROUP_NAMES.unallocated);

      return !(
        this.products.length >= 1 && this.allocatedShippingGroupCount === 1 && !isUnallocated
        || this.products.length === 1 && this.allocatedShippingGroupCount === 0 && isUnallocated
      );
    },

    isStub() {
      return !this.loading && this.widgetData?.type === WIDGET_TYPES.stub;
    },

    // кол-во шиппинг групп в корзине без учета soldout и unallocated
    allocatedShippingGroupCount() {
      const excludeGroups = [SHIPPING_GROUP_NAMES.unallocated, SHIPPING_GROUP_NAMES.soldout];
      return this.SHIPPING_GROUPS
        .filter(({ type }) => !excludeGroups.includes(type)).length;
    },

    widgetData() {
      // кол-во pickup шиппинг-групп, с товарами, среди которых есть availability.pickup.type = store
      const pickupGroupsWithStoreType = this.SHIPPING_GROUPS
        .filter(getPickupGroupsWithStoreType);

      // Если в корзине существует более 1 шиппинг-группы pickup, где хотя бы один товар имеет свойство availability.pickup.type = store
      if (pickupGroupsWithStoreType.length > 1) return this.getWidgetDataForStub();

      const productsCount = this.lineIds.length;

      // магазин, где доступны все товары
      const pointsWithAllProducts = this.points
        .filter(({ availableLinesCount }) => availableLinesCount === productsCount);
      const pointWithAllProducts = pointsWithAllProducts[0];

      // Если есть хотя бы один товар, доступный в единственном магазине и для корзины доступна доставка в магазины
      if (this.productInOnlyOneStore && this.shippingMethods.pickup && pointWithAllProducts) {
        return this.getWidgetDataForOnlyOneStore({ point: pointWithAllProducts });
      }

      // Если нет товаров, доступных только в магазине, и есть только 1 шиппинг-группа pickup, где хотя бы один товар имеет свойство availability.pickup.type = store
      if (!this.isOnlyStoreProduct && pickupGroupsWithStoreType.length === 1) {
        if (!pointWithAllProducts) return this.getWidgetDataForStub();

        const { storeId } = pickupGroupsWithStoreType[0];
        const point = this.getPointById(storeId);

        // если в магазине шип группы пикап есть все товары, то показываем его
        if (point.availableLinesCount === productsCount) {
          return this.getWidgetDataForOnlyOneStore({ point });
        }

        return pointsWithAllProducts.length > 1
          // если есть больше 1 магазина со всеми товарами, то предлагаем выбрать магазин
          ? this.getWidgetDataForOneStore()
          // если только один, то показываем его
          : this.getWidgetDataForOnlyOneStore({ point: pointWithAllProducts })
      }

      const pvzWithAllProducts = this.points
        .find(({ availableLinesCount, type }) => type === 'pvz' && availableLinesCount === productsCount);

      const postamatWithAllProducts = this.points
        .find(({ availableLinesCount, type }) => type === 'postamat' && availableLinesCount === productsCount);

      // Если нет товаров, доступных только в магазине и для корзины доступна доставка курьерской службой
      // и у нераспределенных есть доставка и есть пвз со всеми товарами
      const isCourier = !this.isOnlyStoreProduct && this.shippingMethods.delivery && this.checkUnallocatedDelivery() && pvzWithAllProducts;

      // Если нет товаров, доступных в единственном магазине,
      // для корзины доступна доставка в магазины,
      // есть магазин со всеми товарами, и нет курьерки
      if (
        !this.productInOnlyOneStore
        && this.shippingMethods.pickup
        && pointWithAllProducts
        && !isCourier
      ) {
        return this.getWidgetDataForOneStore();
      }

      // Если нет товаров, доступных только в магазине и для корзины доступна доставка курьерской службой
      // и у нераспределенных есть доставка
      if (isCourier) {
        return this.getWidgetDataForCourier();
      }

      if (!this.productInOnlyOneStore && (postamatWithAllProducts || pvzWithAllProducts)) {
        return this.shippingMethods.delivery
          ? this.getWidgetDataForCourier()
          : this.getWidgetDataForOneOrder();
      }

      // В остальных случаях не существует единого способа доставки
      return this.allocatedShippingGroupCount ? this.getWidgetDataForStub() : null;
    },

    products() {
      return this.SHIPPING_GROUPS.reduce((res, { type, products }) => {
        if (type === SHIPPING_GROUP_NAMES.soldout) return res; // Шиппинг группа soldout не учитывается
        return res.concat(products.map(product => ({
          ...product,
          // товар доступен только для самовывоза из единственного магазина
          isOnlyOneStore: type === SHIPPING_GROUP_NAMES.unallocated && product.availability?.stores === 1,
          // товар доступен только для самовывоза из магазина
          isOnlyStore: type === SHIPPING_GROUP_NAMES.unallocated && product.availability?.stores > 1,
        })));
      }, []);
    },

    // существует товар только для самовывоза из магазина
    isOnlyStoreProduct() {
      return this.products.some(({ isOnlyStore }) => isOnlyStore);
    },

    // товар доступный только для самовывоза из единственного магазина
    productInOnlyOneStore() {
      return this.products.find(({ isOnlyOneStore }) => isOnlyOneStore);
    },

    lineIds() {
      return getLineIds(this.products);
    },

    shippingMethods() {
      return this.SHIPPING_CHANNELS.reduce((res, { available, type }) => {
        res[SHIPPING_METHODS[type]] = !!available;
        return res;
      }, {
        [SHIPPING_METHODS.delivery]: false,
        [SHIPPING_METHODS.pickup]: false,
        [SHIPPING_METHODS.thirdparty]: false,
      });
    },
  },

  watch: {
    lineIds(value, oldValue) {
      if (value.length === oldValue.length && value.every(id => oldValue.includes(id))) return;
      this.getPickupPoints();
    },
  },

  mounted() {
    this.getPickupPoints();
    this.$f.$on('cart-city-changed', this.getPickupPoints);
  },

  beforeDestroy() {
    this.$f.$off('cart-city-changed', this.getPickupPoints);
  },

  methods: {
    async getPickupPoints(city = this.CITY) {
      try {
        this.loading = true;
        this.points = await this.$store.$api.getPickupPoints({
          cityId: city?.id,
          lineIds: this.lineIds,
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    getPointById(pointId) {
      return this.points?.find(({ id }) => id === pointId);
    },

    onClickAction({ pointId, type }) {
      this.$f.$emit('allocate-pickup', { products: this.products, type, pointId });
    },

    getWidgetDataForStub() {
      return {
        type: WIDGET_TYPES.stub,
        title: `Будет ${pluralize(
          this.allocatedShippingGroupCount,
          ['сформирован', 'сформировано', 'сформировано'],
          true,
        )} ${pluralize(this.allocatedShippingGroupCount, ['заказ', 'заказа', 'заказов'])}`,
        description: 'Товары в корзине не могут быть доставлены единым способом',
      };
    },

    getWidgetDataForOnlyOneStore({ point }) {
      const productsCountText = this.getProductCountText();

      return {
        type: WIDGET_TYPES.onlyOneStore,
        title: `Можно забрать все ${productsCountText} одним заказом из магазина по адресу:`,
        description: point?.address,
        action: {
          text: 'Забрать из магазина',
          pointId: point?.id,
          type: DELIVERY_SELECTOR_TYPES.pickup,
        },
      };
    },

    getWidgetDataForOneStore() {
      const freeShippingText = this.getFreeShippingText();
      const productsCountText = this.getProductCountText();

      return {
        type: WIDGET_TYPES.oneStore,
        title: `Можно забрать все ${productsCountText} одним заказом из магазина${freeShippingText}`,
        action: {
          text: 'Выбрать магазин',
          type: DELIVERY_SELECTOR_TYPES.pickup,
        },
      };
    },

    getWidgetDataForOneOrder() {
      const freeShippingText = this.getFreeShippingText();

      return {
        type: WIDGET_TYPES.oneOrder,
        title: `Можно получить все товары одним заказом${freeShippingText}`,
        action: {
          text: 'Выбрать способ получения',
          type: DELIVERY_SELECTOR_TYPES.courier,
        },
      };
    },

    getWidgetDataForCourier() {
      const freeShippingText = this.getFreeShippingText();
      const productsCountText = this.getProductCountText();

      return {
        type: WIDGET_TYPES.courier,
        title: `Можем доставить курьером все ${productsCountText} одним заказом${freeShippingText}`,
        action: {
          text: 'Доставить курьером',
          type: DELIVERY_SELECTOR_TYPES.courier,
        },
      };
    },

    getFreeShippingText() {
      let isFreeShipping = true;
      const deliveryChannel = this.SHIPPING_CHANNELS
        .find(({ type, available }) => type === SHIPPING_METHODS.delivery && available);

      if (deliveryChannel) {
        const freeShipping = +deliveryChannel.freeShipping || 0;
        const subtotal = +this.SUBTOTAL || 0;
        isFreeShipping = subtotal >= freeShipping;
      }

      return isFreeShipping ? ' бесплатно' : '';
    },

    getProductCountText() {
      const productsCount = this.lineIds.length;
      return pluralize(productsCount, ['товар', 'товара', 'товаров']);
    },

    checkUnallocatedDelivery() {
      const unallocatedGroup = this.SHIPPING_GROUPS
        .find(({ type }) => type === SHIPPING_GROUP_NAMES.unallocated);

      return unallocatedGroup
        ? unallocatedGroup.shippingChannels?.some(({ type, available }) => type === SHIPPING_METHODS.delivery && available)
        : true;
    },
  },
};
</script>

<style lang="scss">
.checkout-delivery-widget {
  width: 100%;

  &__wrap {
    background: $blue;
    border-radius: 4px;
    padding: 12px;
  }

  &__text {
    margin-bottom: 8px;
    font-size: 12px;
    line-height: 16px;
    color: $white;
  }

  &__title {
    font-weight: 600;
  }

  &__action {
    height: 32px;
    line-height: 16px;
  }

  &__loader {
    margin: 0 auto;
    color: $white;
  }

  &__stub.ui-notification {
    padding: 12px 16px 12px 12px;
    margin: 0;

    .ui-notification-subtitle,
    .ui-notification-title {
      font-size: 12px;
      line-height: 16px;
    }
  }
}

@include breakpoint("tablet") {
  .checkout-delivery-widget {
    &__text_only-title {
      display: flex;
      align-items: center;
    }
  }

  .checkout-delivery-widget_adaptive {
    .checkout-delivery-widget {
      &__wrap {
        display: flex;
        justify-content: space-between;
      }

      &__text {
        margin-bottom: 0;

        br {
          display: none;
        }
      }

      &__action {
        width: auto;
        margin-left: 12px;
        white-space: nowrap;
      }
    }
  }
}
</style>
